// import { UTCDate } from "@date-fns/utc";
import * as dateFNS from "date-fns";
import isString from "lodash-es/isString";

// Custom date adapter to replace @date-io/date-fns
export const DateFns = {
  format: (date: Date, format: string) => dateFNS.format(date, format),
  parse: (date: string, format: string, referenceDate: Date = new Date()) =>
    dateFNS.parse(date, format, referenceDate),
  isValid: (date: unknown) => dateFNS.isValid(date),
  addDays: (date: Date, amount: number) => dateFNS.addDays(date, amount),
  addMonths: (date: Date, amount: number) => dateFNS.addMonths(date, amount),
  addYears: (date: Date, amount: number) => dateFNS.addYears(date, amount),
  startOfDay: (date: Date) => dateFNS.startOfDay(date),
  endOfDay: (date: Date) => dateFNS.endOfDay(date),
  startOfMonth: (date: Date) => dateFNS.startOfMonth(date),
  endOfMonth: (date: Date) => dateFNS.endOfMonth(date),
  startOfYear: (date: Date) => dateFNS.startOfYear(date),
  endOfYear: (date: Date) => dateFNS.endOfYear(date),
  getYear: (date: Date) => dateFNS.getYear(date),
  getMonth: (date: Date) => dateFNS.getMonth(date),
  getDate: (date: Date) => dateFNS.getDate(date),
  getHours: (date: Date) => dateFNS.getHours(date),
  getMinutes: (date: Date) => dateFNS.getMinutes(date),
  getSeconds: (date: Date) => dateFNS.getSeconds(date),
  setYear: (date: Date, year: number) => dateFNS.setYear(date, year),
  setMonth: (date: Date, month: number) => dateFNS.setMonth(date, month),
  setDate: (date: Date, day: number) => dateFNS.setDate(date, day),
  setHours: (date: Date, hours: number) => dateFNS.setHours(date, hours),
  setMinutes: (date: Date, minutes: number) =>
    dateFNS.setMinutes(date, minutes),
  setSeconds: (date: Date, seconds: number) =>
    dateFNS.setSeconds(date, seconds),
  isAfter: (date: Date, dateToCompare: Date) =>
    dateFNS.isAfter(date, dateToCompare),
  isBefore: (date: Date, dateToCompare: Date) =>
    dateFNS.isBefore(date, dateToCompare),
  isEqual: (date: Date, dateToCompare: Date) =>
    dateFNS.isEqual(date, dateToCompare),
  isSameDay: (date: Date, dateToCompare: Date) =>
    dateFNS.isSameDay(date, dateToCompare),
  isSameMonth: (date: Date, dateToCompare: Date) =>
    dateFNS.isSameMonth(date, dateToCompare),
  isSameYear: (date: Date, dateToCompare: Date) =>
    dateFNS.isSameYear(date, dateToCompare),
  differenceInDays: (date: Date, dateToCompare: Date) =>
    dateFNS.differenceInDays(date, dateToCompare),
  differenceInMonths: (date: Date, dateToCompare: Date) =>
    dateFNS.differenceInMonths(date, dateToCompare),
  differenceInYears: (date: Date, dateToCompare: Date) =>
    dateFNS.differenceInYears(date, dateToCompare),
  eachDayOfInterval: (interval: { start: Date; end: Date }) =>
    dateFNS.eachDayOfInterval(interval),
  parseISO: (date: string) => dateFNS.parseISO(date),
  formatISO: (date: Date) => dateFNS.formatISO(date),
};

export const isDateString = (value: string): boolean => {
  if (value.length < 20) return false;
  const newValue = Date.parse(value);
  return !Number.isNaN(newValue);
};

export const dateFormat = (
  date: string | Date,
  format: string = "yyyy-MM-dd HH:mm:ss",
): string => {
  try {
    return dateFNS.format(new Date(date), format);
  } catch (ex) {
    // eslint-disable-next-line no-console
    console.warn(ex);
    return date as string;
  }
};

export const dateFormatUTC = (
  date: string | Date | number,
  format: string = "yyyy-MM-dd HH:mm:ss",
): string => {
  try {
    if (date) {
      const localDate = new Date(date);
      const utcDate =
        localDate.getTime() + localDate.getTimezoneOffset() * 60000;
      return dateFNS.format(new Date(utcDate), format);
    } else {
      return "";
    }
  } catch (ex) {
    // eslint-disable-next-line no-console
    console.warn(ex);
    return date.toString();
  }
};

/**
 * Localized, viewer-timezone timestamp — the shared list-table standard
 * (TRUST-12986). Formats with `Intl.DateTimeFormat` in the active i18n locale
 * and the browser's own timezone, so "Last Updated" reads in the viewer's local
 * time. Pair it with `dateFormatUTCWithLabel` for the hover tooltip, which keeps
 * the absolute UTC value one hover away for audit/comparison work.
 *
 * Falls back to the raw value (never throws) so a malformed API date degrades to
 * text instead of blanking the cell.
 */
const DEFAULT_DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  dateStyle: "medium",
  timeStyle: "short",
};

/**
 * Constructing an `Intl.DateTimeFormat` is one of the more expensive routine
 * operations in JS, and the default options are shared by every caller — so
 * formatters are cached per locale rather than rebuilt per value. Callers
 * passing their own `options` skip the cache.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

const getDefaultFormatter = (locale?: string): Intl.DateTimeFormat => {
  const key = locale || "";
  const cached = formatterCache.get(key);
  if (cached) return cached;
  // `undefined` locale lets Intl fall back to the runtime default.
  const formatter = new Intl.DateTimeFormat(
    locale || undefined,
    DEFAULT_DATE_TIME_OPTIONS,
  );
  formatterCache.set(key, formatter);
  return formatter;
};

export const formatDateTimeLocal = (
  date: string | Date | number,
  locale?: string,
  options?: Intl.DateTimeFormatOptions,
): string => {
  if (!date) return "";
  try {
    const parsed = new Date(date);
    if (!dateFNS.isValid(parsed)) return String(date);
    const formatter = options
      ? new Intl.DateTimeFormat(locale || undefined, options)
      : getDefaultFormatter(locale);
    return formatter.format(parsed);
  } catch (ex) {
    // eslint-disable-next-line no-console
    console.warn(ex);
    return String(date);
  }
};

export const dateFormatUTCWithLabel = (
  date: string | Date,
  missingDateLabel = "-",
) => {
  const formattedDate = dateFormatUTC(date, "yyyy-MM-dd HH:mm:ss");
  return formattedDate ? `${formattedDate} UTC` : missingDateLabel;
};

export const getLastDays = (daysInNumber = 30) => {
  const { eachDayOfInterval } = dateFNS;
  return eachDayOfInterval(getLastDates(daysInNumber));
};

export const getLastDates = (daysInNumber = 30) => {
  const { subDays } = dateFNS;
  // const today = new Date();
  const presentDay = new Date();
  const start = subDays(presentDay, daysInNumber);
  const end = subDays(presentDay, 0);
  return { start: start as Date, end: end as Date };
};

export const getDiffDates = (date: Date, daysInNumber = 30) => {
  const { addDays } = dateFNS;
  const beforeDays = addDays(date, daysInNumber);
  return { start: beforeDays, end: date };
};

export const getDays = (startDate: Date, endDate: Date) => {
  return dateFNS.differenceInDays(endDate, startDate);
};

export const getDateDistance = (
  date: Date | string,
  baseDate: Date = new Date(),
  addSuffix = true,
  style: Intl.RelativeTimeFormatStyle = "long",
) => {
  const parseDate = isString(date) ? new Date(date) : date;

  if (!dateFNS.isValid(parseDate) || !dateFNS.isValid(baseDate)) {
    return "";
  }

  if (!addSuffix) {
    return dateFNS.formatDistanceStrict(parseDate, baseDate);
  }

  return dateFNS.intlFormatDistance(parseDate, baseDate, {
    style: style,
  });
};

export default dateFNS;
